import { Project } from '../types';
import { generateKimapUniqueId } from './objExporter';
import { OBJExporter } from './objExporter';
import { encryptToKimap } from './kimapEncryption';
import { exportProjectAsMTL, encryptMTL } from './mtlExporter';
import { encryptKidata } from './kidataExporter';
import { buildKidataContentWithModels } from './kidataExporter';
import JSZip from 'jszip';
import axios from 'axios';
import { getAxiosIns } from '../server/api';

/**
 * 地图发布相关接口
 */

// 上传响应格式
export interface MapPublishResponse {
  success: boolean;
  message?: string;
  mapId?: string;        // 服务器返回的地图ID
  mapUrl?: string;       // 地图访问URL
  uniqueId?: string;     // 发布的kimap唯一ID
}

/**
 * 导出Kimap文件内容（不下载）
 */
export function exportKimapContent(project: Project, uniqueId: string): string {
  const exporter = new OBJExporter();
  const objContent = exporter.exportProject(project, uniqueId);
  return encryptToKimap(objContent);
}

/**
 * 导出Kitheme文件内容（不下载）
 */
export function exportKithemeContent(project: Project): string {
  const mtlContent = exportProjectAsMTL(project);
  return encryptMTL(mtlContent);
}

/**
 * 导出Kidata文件内容（不下载）
 * 内部调用 buildKidataContentWithModels，会并发拉取所有用到的3D模型源文件并嵌入到 kidata 中
 */
export async function exportKidataContent(project: Project, _uniqueId: string): Promise<string> {
  const { jsonStr } = await buildKidataContentWithModels(project);
  return encryptKidata(jsonStr);
}

/**
 * 导出项目并打包为ZIP
 * @param project 项目数据
 * @returns 包含uniqueId、projectName和zipBlob的包
 */
export async function exportProjectAsMapZip(project: Project): Promise<{
  uniqueId: string;
  projectName: string;
  zipBlob: Blob;
  fileCount: number;
}> {
  const uniqueId = generateKimapUniqueId();
  const projectName = uniqueId; // 与kimap文件名一致

  // 检查是否有3D家具模型
  const has3DModels = project.floors.some((floor: any) =>
    floor.layers.some((layer: any) =>
      layer.elements.some((el: any) => el.type === 'model3d')
    )
  );

  // 创建ZIP实例
  const zip = new JSZip();

  // 添加kimap文件
  const kimapContent = exportKimapContent(project, uniqueId);
  zip.file(`${uniqueId}.kimap`, kimapContent);

  // 添加kitheme文件
  const kithemeContent = exportKithemeContent(project);
  zip.file(`${uniqueId}.kitheme`, kithemeContent);

  // 如果有3D家具模型，添加kidata文件
  let fileCount = 2;
  if (has3DModels) {
    const kidataContent = await exportKidataContent(project, uniqueId);
    zip.file(`${uniqueId}.kidata`, kidataContent);
    fileCount = 3;
  }

  // 添加元数据JSON
  const metadata = {
    uniqueId,
    projectName,
    exportTime: new Date().toISOString(),
    has3DModels,
    fileCount,
  };
  zip.file('metadata.json', JSON.stringify(metadata, null, 2));

  // 生成ZIP文件
  const zipBlob = await zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });

  return {
    uniqueId,
    projectName,
    zipBlob,
    fileCount,
  };
}

/**
 * 上传ZIP文件到服务器
 * @param zipBlob ZIP文件内容
 * @param projectName 项目名称（作为文件标识）
 * @param fileCount 文件数量
 */
export async function uploadMapZip(
  zipBlob: Blob,
  projectName: string,
  fileCount: number
): Promise<MapPublishResponse> {
  try {
    const formData = new FormData();
    formData.append('file', zipBlob, `${projectName}.zip`);
    formData.append('name', projectName);
    formData.append('fileCount', String(fileCount));

    const response = await getAxiosIns().post('/map/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    console.log('地图发布响应:', response.data);

    if (response.data && response.data.code === 200) {
      return {
        success: true,
        mapId: response.data.mapId,
        mapUrl: response.data.mapUrl,
        uniqueId: projectName, // 返回uniqueId
      };
    } else {
      return {
        success: false,
        message: response.data?.message,
      };
    }
  } catch (error: any) {
    console.error('地图发布失败:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || '网络请求失败',
    };
  }
}

/**
 * 发布地图完整流程（ZIP打包上传，使用apiJson格式）
 * @param project 项目数据
 */
export async function publishMap(project: Project): Promise<MapPublishResponse> {
  // 1. 导出并打包成ZIP
  const { uniqueId, projectName, zipBlob, fileCount } = await exportProjectAsMapZip(project);

  console.log('📦 地图ZIP包准备完成:');
  console.log(`  - UniqueId: ${uniqueId}`);
  console.log(`  - ProjectName: ${projectName}`);
  console.log(`  - 文件数量: ${fileCount}`);
  console.log(`  - ZIP大小: ${(zipBlob.size / 1024).toFixed(2)} KB`);

  // 2. 上传ZIP到服务器（使用apiJson格式）
  return await uploadMapZip(zipBlob, projectName, fileCount);
}

/**
 * 保存区域数据到服务器
 * 更新 res_url 中的 kimap 参数为新发布的值
 * 使用与登录相同的代理方式
 */
export async function saveAreaData(
  area: { id: number; customer_id: number; name: string; res_url: string; n: string; p0: string; create_time: string; remark?: string },
  kimapName: string
): Promise<void> {
  // 替换 res_url 中现有的 kimap 参数为新值
  let resUrl = area.res_url;
  try {
    const urlObj = new URL(resUrl);
    urlObj.searchParams.set('kimap', kimapName);
    resUrl = urlObj.toString();
  } catch (e) {
    // 如果 res_url 不是有效URL，手动替换 kimap 参数
    const kimapRegex = /([?&]?)kimap=[^&]*/;
    if (kimapRegex.test(resUrl)) {
      resUrl = resUrl.replace(kimapRegex, `$1kimap=${kimapName}`);
    } else {
      resUrl = resUrl + (resUrl.includes('?') ? '&' : '?') + 'kimap=' + kimapName;
    }
  }

  // 使用与登录相同的代理方式（相对路径 /indoorService）
  const client = axios.create({
    baseURL: '/indoorService',
    timeout: 20000,
  });

  await client.post('/put', {
    'Area': {
      ...area,
      res_url: resUrl,
    },
    "tag": "Area",
    '@role': 'OWNER',
  });
}
